import {memo} from 'react';
import type {AlgorithmPatternRawData, PatternDetectResult} from '../../../../types/patternRecognition';
import type {AIAnalysis} from '../types';
import {formatRelativeTime, formatSignalTime, formatTimeTooltip, parseCreatedAt} from '../utils/patternTime';
import {resolveFibonacciSwingRange, resolveTouchedFibonacciRatio} from '../utils/patternOverlayLines';
import {AiAnalysisReport} from './AiAnalysisReport';

interface Props {
    pattern: PatternDetectResult;
    selected: boolean;
    viewChartActive: boolean;
    onSelect: () => void;
    onViewChart: () => void;
    isAiAnalyzing: boolean;
    aiAnalysis?: AIAnalysis;
    isAiExpanded: boolean;
    onAiClick: () => void;
    canTriggerAi: boolean;
    isPocPage: boolean;
    interval?: string;
    quickTradeEnabled: boolean;
    validationError?: string | null;
    expiryReason?: string | null;
    onGoTrade?: (direction: 'FORWARD' | 'REVERSE') => void;
}

const scaleLabel: Record<string, string> = {LARGE: '大范围', MEDIUM: '中区间', SMALL: '小区间'};

export const AlgorithmPatternCard = memo(function AlgorithmPatternCard({
                                                                           pattern,
                                                                           selected,
                                                                           viewChartActive,
                                                                           onSelect,
                                                                           onViewChart,
                                                                           isAiAnalyzing,
                                                                           aiAnalysis,
                                                                           isAiExpanded,
                                                                           onAiClick,
                                                                           canTriggerAi,
                                                                           isPocPage,
                                                                           interval,
                                                                           quickTradeEnabled,
                                                                           validationError,
                                                                           expiryReason,
                                                                           onGoTrade,
                                                                       }: Props) {
    const data = pattern.rawData as AlgorithmPatternRawData | null;
    const levels = data?.keyLevels;
    const scale = data?.scale ?? 'SMALL';
    const ratio = resolveTouchedFibonacciRatio(data);
    // 波段按区间语义展示：起点 → Swing 终点（klineEndTime 为确认K线时间，仅作数据缺失时回退）
    const swingRange = resolveFibonacciSwingRange(data);
    const swingStartTime = swingRange?.startTime ?? pattern.klineStartTime;
    const swingEndTime = swingRange?.endTime ?? pattern.klineEndTime;
    const recognizedAt = parseCreatedAt(pattern.createdAt);
    const hasData = !!aiAnalysis;
    return <div className={`pattern-card${selected ? ' selected' : ''}${pattern.direction < 0 ? ' bear-card' : ''}`}
                onClick={onSelect}>
        <div className="pc-header">
            <div className="pc-name">{pattern.patternName}</div>
            <div
                className={`pc-dir ${pattern.direction > 0 ? 'bull' : 'bear'}`}>{pattern.direction > 0 ? '看多' : '看空'}</div>
        </div>
        <div className="pc-meta">
            <span style={{fontSize: 10, color: 'var(--pt-amber)'}}>{scaleLabel[scale] ?? scale}</span>
            <div className="pc-conf"><span>置信度</span>
                <div className="conf-bar">
                    <div className={`conf-fill ${pattern.direction > 0 ? 'bull' : 'bear'}`}
                         style={{width: `${pattern.confidence}%`}}/>
                </div>
                <span>{pattern.confidence}%</span></div>
        </div>
        <div className="pc-time">
            <div>波段 {formatSignalTime(swingStartTime)} → {formatSignalTime(swingEndTime)}</div>
            {ratio != null && <div style={{fontSize: 10, marginTop: 2}}>命中回撤 {(Number(ratio) * 100).toFixed(1)}% ·
                确认价 {levels?.confirmationPrice?.toFixed(2) ?? '--'}</div>}
            <div style={{fontSize: 10, color: 'var(--pt-text-dim)', marginTop: 2}}
                 title={null == recognizedAt ? undefined : formatTimeTooltip(recognizedAt)}>
                识别于 {null == recognizedAt ? '--' : formatSignalTime(recognizedAt)}
                {null == recognizedAt ? '' : ` · ${formatRelativeTime(recognizedAt)}`}
            </div>
        </div>
        <button type="button" className={`pc-view-chart-btn${viewChartActive ? ' active' : ''}`} onClick={event => {
            event.stopPropagation();
            onViewChart();
        }}>
            在图表中查看
        </button>

        {!isPocPage || hasData || pattern.hasAiAnalysis ? (
            <div className="pc-ai-section">
                <button
                    className={`pc-ai-btn${isAiAnalyzing ? ' loading' : ''}${hasData || pattern.hasAiAnalysis ? ' has-data' : ''}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        onAiClick();
                    }}
                    disabled={isAiAnalyzing || !canTriggerAi}
                    title={isAiAnalyzing ? 'AI 分析中...' : hasData ? '查看AI解读' : '触发AI分析'}
                >
                    {isAiAnalyzing ? '⏳' : hasData ? '📋' : '🤖'}
                </button>
            </div>
        ) : null}

        {isAiExpanded && aiAnalysis && (
            <AiAnalysisReport
                aiData={aiAnalysis}
                holdingPeriodText={aiAnalysis.holdingPeriodText}
                onGoTrade={onGoTrade}
                quickTradeEnabled={quickTradeEnabled}
                validationError={validationError}
                expiryReason={expiryReason}
                signalDirection={pattern.direction as 1 | -1 | 0}
                signalConfidence={pattern.confidence}
            />
        )}
        {isAiExpanded && isAiAnalyzing && <div className="pc-ai-report">AI 分析中...</div>}
    </div>;
});
